import { useEffect, useRef, type CSSProperties, type ReactNode } from 'react';
import { useFeedback } from './Feedback';

/**
 * Fundo escuro de todo modal do painel (auditoria A2). Cuida do que todo modal precisa:
 * - Esc fecha (só o modal de cima, quando um abre por cima de outro);
 * - clique no fundo fecha;
 * - o Tab fica preso dentro do modal e, ao fechar, o foco volta ao botão que o abriu;
 * - a página de trás não rola enquanto o modal está aberto.
 *
 * `pedirParaFechar` é chamado no Esc e no clique fora. Com `protegerDigitacao`, se a pessoa já
 * digitou ou escolheu algo dentro do modal, pergunta antes de descartar. Os botões Cancelar e X de
 * cada modal continuam fechando direto (ali a intenção é clara).
 * O conteúdo (a caixa branca) continua sendo desenhado por cada modal.
 */
interface ModalProps {
  pedirParaFechar: () => void;
  children: ReactNode;
  /** id do título do modal, para leitores de tela. */
  rotuloId?: string;
  /** Clique no fundo fecha? (padrão: sim). */
  fecharNoFundo?: boolean;
  /** Pergunta antes de fechar pelo Esc ou pelo fundo se algo foi digitado. */
  protegerDigitacao?: boolean;
  zIndex?: number;
  style?: CSSProperties;
}

const SELETOR_FOCAVEL =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

// Pilha dos modais abertos: o Esc vale só para o de cima.
const pilha: symbol[] = [];

export function Modal({ pedirParaFechar, children, rotuloId, fecharNoFundo = true, protegerDigitacao = false, zIndex = 1000, style }: ModalProps) {
  const { confirmar } = useFeedback();
  const fundoRef = useRef<HTMLDivElement>(null);
  const digitou = useRef(false);
  const fecharRef = useRef<() => void>(() => {});
  fecharRef.current = async () => {
    if (protegerDigitacao && digitou.current) {
      const descartar = await confirmar({
        titulo: 'Descartar o que foi preenchido?',
        mensagem: 'As informações deste formulário ainda não foram salvas.',
        confirmar: 'Descartar',
        cancelar: 'Continuar editando',
        perigo: true,
      });
      if (!descartar) return;
    }
    pedirParaFechar();
  };
  // O clique só fecha se começou e terminou no fundo (arrastar texto de dentro para fora não fecha).
  const cliqueComecouNoFundo = useRef(false);

  useEffect(() => {
    const id = Symbol('modal');
    pilha.push(id);
    const focoAnterior = document.activeElement as HTMLElement | null;
    const overflowAnterior = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // Foco inicial: o primeiro campo do modal (ou o próprio modal), se nada lá dentro já tiver o foco.
    const fundo = fundoRef.current;
    if (fundo && !fundo.contains(document.activeElement)) {
      const campo = fundo.querySelector<HTMLElement>('input:not([disabled]):not([type="hidden"]):not([type="checkbox"]), textarea, select');
      (campo ?? fundo.querySelector<HTMLElement>(SELETOR_FOCAVEL) ?? fundo).focus();
    }

    const aoTeclar = (e: KeyboardEvent) => {
      if (pilha[pilha.length - 1] !== id) return;
      if (e.key === 'Escape') {
        e.stopPropagation();
        fecharRef.current();
        return;
      }
      if (e.key !== 'Tab' || !fundoRef.current) return;
      const focaveis = Array.from(fundoRef.current.querySelectorAll<HTMLElement>(SELETOR_FOCAVEL)).filter(
        (el) => el.offsetParent !== null || el === document.activeElement
      );
      if (focaveis.length === 0) {
        e.preventDefault();
        return;
      }
      const primeiro = focaveis[0];
      const ultimo = focaveis[focaveis.length - 1];
      const ativo = document.activeElement;
      if (e.shiftKey && (ativo === primeiro || !fundoRef.current.contains(ativo))) {
        e.preventDefault();
        ultimo.focus();
      } else if (!e.shiftKey && (ativo === ultimo || !fundoRef.current.contains(ativo))) {
        e.preventDefault();
        primeiro.focus();
      }
    };
    document.addEventListener('keydown', aoTeclar);

    return () => {
      document.removeEventListener('keydown', aoTeclar);
      const i = pilha.indexOf(id);
      if (i >= 0) pilha.splice(i, 1);
      if (pilha.length === 0) document.body.style.overflow = overflowAnterior;
      if (focoAnterior && document.contains(focoAnterior)) focoAnterior.focus();
    };
  }, []);

  return (
    <div
      ref={fundoRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby={rotuloId}
      tabIndex={-1}
      onInput={() => { digitou.current = true; }}
      onMouseDown={(e) => { cliqueComecouNoFundo.current = e.target === e.currentTarget; }}
      onClick={(e) => {
        if (fecharNoFundo && cliqueComecouNoFundo.current && e.target === e.currentTarget) fecharRef.current();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.7)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex,
        padding: 'var(--space-4)',
        outline: 'none',
        ...style,
      }}
    >
      {children}
    </div>
  );
}
